import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOrderContext } from '../../providers/OrderProvider';
import { approvalsApiSlice } from '../../slices/approvalsApiSlice';
import { isRecentLocalOrderMutation } from '../../utils/order/orderLocalMutationGuard';
import {
	buildOrderCollaborationSnapshot,
	DEFAULT_ORDER_COLLABORATION_PRESET,
	isOrderDetailsMissingError,
	ORDER_COLLABORATION_PRESETS,
} from '../../utils/order/orderRemoteCollaboration';

/**
 * 订单 Modal 双人协作：仅当「他人」改单时刷新并提示观察者（本机刚点的按钮不走此逻辑）
 * @param {'approval'|'delivery'|'payment'|'accept'} preset
 * @param {{ enabled?: boolean, isLocalAction?: boolean, onBeforeRefetch?: () => Promise<void>|void }} options
 */
export const useOrderRemoteCollaboration = (preset, { enabled = true, isLocalAction = false, onBeforeRefetch } = {}) => {
	const dispatch = useDispatch();
	const { userInfo } = useSelector((state) => state.auth);
	const { order, refetch, refetchProcessingOrderLists, orderDetailsError } = useOrderContext();
	const config = ORDER_COLLABORATION_PRESETS[preset] ?? DEFAULT_ORDER_COLLABORATION_PRESET;
	const shouldShowNotice = config.showNotice !== false;

	const [openedTracked, setOpenedTracked] = useState(false);
	const [remoteNotice, setRemoteNotice] = useState(null);
	const [noticeExtras, setNoticeExtras] = useState({});
	const [isRemoteSyncing, setIsRemoteSyncing] = useState(false);
	const snapshotRef = useRef(null);
	const remoteNoticeShownRef = useRef(false);

	useEffect(() => {
		if (!enabled) {
			setOpenedTracked(false);
			setRemoteNotice(null);
			setNoticeExtras({});
			setIsRemoteSyncing(false);
			snapshotRef.current = null;
			remoteNoticeShownRef.current = false;
			return;
		}
		if (shouldShowNotice && order && config.trackOpened(order)) {
			setOpenedTracked(true);
			snapshotRef.current = buildOrderCollaborationSnapshot(order);
		}
	}, [enabled, order, config, shouldShowNotice]);

	const showNoticeIfNeeded = useCallback(
		(nextOrder, extras = {}) => {
			if (!shouldShowNotice || !openedTracked || remoteNoticeShownRef.current) return;
			const mergedExtras = { orderId: snapshotRef.current?.orderId, ...extras };
			if (!config.noticeWhen(nextOrder, openedTracked, snapshotRef.current, mergedExtras)) return;
			const message = config.message(nextOrder, mergedExtras);
			if (!message) return;
			remoteNoticeShownRef.current = true;
			setNoticeExtras(mergedExtras);
			setRemoteNotice(message);
		},
		[config, openedTracked, shouldShowNotice],
	);

	useEffect(() => {
		if (!enabled || isLocalAction || !openedTracked) return;
		if (!isOrderDetailsMissingError(orderDetailsError)) return;
		showNoticeIfNeeded(order, { orderMissing: true });
	}, [enabled, isLocalAction, openedTracked, order, orderDetailsError, showNoticeIfNeeded]);

	const handleRemoteOrderUpdate = useCallback(
		async (payload) => {
			if (!enabled || isLocalAction) return;

			const orderId = order?._id;
			if (!orderId) return;

			if (isRecentLocalOrderMutation(orderId)) return;

			const sourceUserId = payload?.sourceUserId != null ? String(payload.sourceUserId) : null;
			if (sourceUserId && userInfo?._id && sourceUserId === String(userInfo._id)) {
				return;
			}

			setIsRemoteSyncing(true);
			try {
				let approvalStatus;
				if (typeof onBeforeRefetch === 'function') {
					const before = await onBeforeRefetch();
					approvalStatus = before?.data?.status;
				}

				let freshOrder;
				let orderMissing = false;
				if (typeof refetch === 'function') {
					const result = await refetch();
					freshOrder = result?.data;
					orderMissing = isOrderDetailsMissingError(result?.error);
				}

				dispatch(approvalsApiSlice.util.invalidateTags(['Approval']));
				refetchProcessingOrderLists?.();

				const extras = { orderMissing, approvalStatus };
				const nextOrder = freshOrder ?? order;
				showNoticeIfNeeded(nextOrder, extras);
			} finally {
				setIsRemoteSyncing(false);
			}
		},
		[
			enabled,
			isLocalAction,
			onBeforeRefetch,
			refetch,
			dispatch,
			refetchProcessingOrderLists,
			order,
			showNoticeIfNeeded,
			userInfo?._id,
		],
	);

	return {
		observerNotice: remoteNotice,
		observerTone: config.tone(order, noticeExtras),
		observerIcon: config.icon(order, noticeExtras),
		collaborationLocked: shouldShowNotice && Boolean(remoteNotice),
		isRemoteSyncing,
		handleRemoteOrderUpdate,
	};
};
